import { useCallback, useEffect, useState } from 'react';
import { api, type ExchangeKeyStatus, type ModeState, type Settings } from '../lib/api.ts';

/**
 * Settings, exchange keys and 2FA (FR-4.5, FR-6.1, FR-6.2).
 *
 * Percentages are edited as percent because that is how exchanges publish
 * fees; they are stored as fractions. The screen is explicit about the two
 * things that surprise people: a settings change needs a detector restart to
 * take effect, and what permissions a key needs.
 *
 * That second one is NOT a constant. Transfer mode moves coins between
 * exchanges, so its keys must be withdrawal-ENABLED; pre-positioned mode never
 * transfers and its keys must be withdrawal-disabled. This screen used to
 * state the pre-positioned rule unconditionally, which under transfer mode
 * told the owner to create a key that cannot complete a trade — and the
 * failure lands after the buy has filled, leaving a coin they did not want.
 * The wording comes from the server, which knows the mode.
 */

export function SettingsPage({ mode }: { mode: ModeState | null }) {
  const [settings, setSettings] = useState<Settings | null>(null);
  const [exchanges, setExchanges] = useState<ExchangeKeyStatus[]>([]);
  const [form, setForm] = useState<Record<string, number | boolean>>({});
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<{ kind: 'ok' | 'bad' | 'warn'; text: string } | null>(null);

  const load = useCallback(async () => {
    const data = await api.settings();
    setSettings(data.settings);
    setExchanges(data.exchanges);
    setForm({
      thresholdPct: data.settings.thresholdPct * 100,
      bufferPct: data.settings.bufferPct * 100,
      alertMinNetPct: data.settings.alertMinNetPct * 100,
      defaultSizeQuote: data.settings.defaultSizeQuote,
      maxTradeSizeQuote: data.settings.maxTradeSizeQuote,
      stalenessMs: data.settings.stalenessMs,
      alertCooldownMs: data.settings.alertCooldownMs,
      alertsEnabled: data.settings.alertsEnabled,
      telegramEnabled: data.settings.telegramEnabled,
      emailEnabled: data.settings.emailEnabled,
    });
  }, []);

  useEffect(() => {
    void load().catch((err) => setMessage({ kind: 'bad', text: String(err.message ?? err) }));
  }, [load]);

  const save = async () => {
    setSaving(true);
    setMessage(null);
    try {
      const result = await api.updateSettings(form);
      setSettings(result.settings);
      setMessage({
        kind: 'warn',
        text: 'Saved. Restart the detector for the new values to take effect on the running engine.',
      });
    } catch (err) {
      setMessage({ kind: 'bad', text: err instanceof Error ? err.message : 'save failed' });
    } finally {
      setSaving(false);
    }
  };

  const field = (key: string, value: number | boolean) => setForm((f) => ({ ...f, [key]: value }));

  if (!settings) return <div className="empty">Loading settings…</div>;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Settings</h1>
          <div className="sub">
            Stored in the database, not in <code>.env</code> — changes survive a redeploy.
          </div>
        </div>
      </div>

      {message && <div className={`notice ${message.kind}`}>{message.text}</div>}

      <div className="grid cols-2">
        <div className="card">
          <div className="card-head">
            <h2>Detection</h2>
          </div>
          <div className="card-body">
            <div className="field">
              <label>Minimum net profit (%)</label>
              <input
                type="number"
                step="0.01"
                value={String(form.thresholdPct ?? '')}
                onChange={(e) => field('thresholdPct', Number(e.target.value))}
              />
              <div className="help">
                Only opportunities clearing this after both taker fees and the buffer are surfaced.
                Lower it to see more and measure whether an edge exists; raise it to cut noise.
              </div>
            </div>

            <div className="field">
              <label>Slippage / safety buffer (%)</label>
              <input
                type="number"
                step="0.01"
                value={String(form.bufferPct ?? '')}
                onChange={(e) => field('bufferPct', Number(e.target.value))}
              />
              <div className="help">
                Subtracted from every net figure. It absorbs the difference between the quoted top
                of book and what a market order actually fills at.
              </div>
            </div>

            <div className="field">
              <label>Staleness window (ms)</label>
              <input
                type="number"
                step="100"
                value={String(form.stalenessMs ?? '')}
                onChange={(e) => field('stalenessMs', Number(e.target.value))}
              />
              <div className="help">
                Prices older than this are excluded from calculation entirely, not merely flagged.
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <h2>Trade sizing</h2>
          </div>
          <div className="card-body">
            <div className="field">
              <label>Default trade size</label>
              <input
                type="number"
                step="50"
                value={String(form.defaultSizeQuote ?? '')}
                onChange={(e) => field('defaultSizeQuote', Number(e.target.value))}
              />
              <div className="help">Pre-filled at the confirmation step; the owner can change it there.</div>
            </div>

            <div className="field">
              <label>Maximum trade size</label>
              <input
                type="number"
                step="100"
                value={String(form.maxTradeSizeQuote ?? '')}
                onChange={(e) => field('maxTradeSizeQuote', Number(e.target.value))}
              />
              <div className="help">
                A hard cap enforced at confirmation. The full risk suite — daily loss cap and
                kill-switch — is Phase 2; this is the Phase 1 guard.
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-head">
            <h2>Alerts</h2>
          </div>
          <div className="card-body">
            <div className="field">
              <label>
                <input
                  type="checkbox"
                  checked={Boolean(form.alertsEnabled)}
                  onChange={(e) => field('alertsEnabled', e.target.checked)}
                  style={{ width: 'auto', marginRight: 8 }}
                />
                Push alerts enabled
              </label>
            </div>
            <div className="row">
              <div className="field" style={{ flex: 1 }}>
                <label>
                  <input
                    type="checkbox"
                    checked={Boolean(form.telegramEnabled)}
                    onChange={(e) => field('telegramEnabled', e.target.checked)}
                    style={{ width: 'auto', marginRight: 8 }}
                  />
                  Telegram
                </label>
              </div>
              <div className="field" style={{ flex: 1 }}>
                <label>
                  <input
                    type="checkbox"
                    checked={Boolean(form.emailEnabled)}
                    onChange={(e) => field('emailEnabled', e.target.checked)}
                    style={{ width: 'auto', marginRight: 8 }}
                  />
                  Email
                </label>
              </div>
            </div>

            <div className="field">
              <label>Alert floor (%)</label>
              <input
                type="number"
                step="0.01"
                value={String(form.alertMinNetPct ?? '')}
                onChange={(e) => field('alertMinNetPct', Number(e.target.value))}
              />
              <div className="help">Never alert below this, even if it clears the display threshold.</div>
            </div>

            <div className="field">
              <label>Cooldown per route (ms)</label>
              <input
                type="number"
                step="30000"
                value={String(form.alertCooldownMs ?? '')}
                onChange={(e) => field('alertCooldownMs', Number(e.target.value))}
              />
              <div className="help">
                The same pair and direction will not alert again inside this window, unless the
                opportunity gets materially better. A muted alert channel is worse than none.
              </div>
            </div>
          </div>
        </div>

        <ExchangeKeys exchanges={exchanges} onChange={setExchanges} mode={mode} />
      </div>

      <div className="row" style={{ marginBottom: 24 }}>
        <button className="btn primary" onClick={() => void save()} disabled={saving}>
          {saving ? 'Saving…' : 'Save settings'}
        </button>
        <button className="btn" onClick={() => void load()} disabled={saving}>
          Discard changes
        </button>
      </div>

      <TwoFactor />
    </>
  );
}

// --------------------------------------------------------------- exchange keys

function ExchangeKeys({
  exchanges,
  onChange,
  mode,
}: {
  exchanges: ExchangeKeyStatus[];
  onChange: (next: ExchangeKeyStatus[]) => void;
  mode: ModeState | null;
}) {
  const [editing, setEditing] = useState<string | null>(null);
  const [apiKey, setApiKey] = useState('');
  const [secret, setSecret] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const submit = async (exchange: string) => {
    setSaving(true);
    setError(null);
    try {
      const result = await api.saveKeys(exchange, { apiKey, secret, password: password || undefined });
      onChange(result.exchanges);
      setEditing(null);
      setApiKey('');
      setSecret('');
      setPassword('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'failed to save keys');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card">
      <div className="card-head">
        <h2>Exchange API keys</h2>
      </div>
      <div className="card-body">
        <div className={`notice ${mode?.mode === 'transfer' ? 'bad' : 'warn'}`}>
          {mode ? (
            <>
              {mode.keyGuidance}
              {mode.requiredPermissions.length > 0 && (
                <>
                  <br />
                  <br />
                  Permissions needed: <strong>{mode.requiredPermissions.join(', ')}</strong>.
                </>
              )}
            </>
          ) : (
            'Checking which permissions this deployment needs…'
          )}
          <br />
          <br />
          Keys are encrypted at rest with the server's master key and are never shown again after
          saving.
        </div>

        {exchanges.map((ex) => (
          <div key={ex.exchange} style={{ marginBottom: 14 }}>
            <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <strong>{ex.label}</strong>{' '}
                {ex.configured ? (
                  <span className="mono faint" style={{ fontSize: 12 }}>
                    {ex.apiKeyMasked}
                  </span>
                ) : (
                  <span className="pill">not configured</span>
                )}
              </div>
              <button
                className="btn small"
                onClick={() => {
                  setEditing(editing === ex.exchange ? null : ex.exchange);
                  setError(null);
                }}
              >
                {editing === ex.exchange ? 'Cancel' : ex.configured ? 'Replace' : 'Add keys'}
              </button>
            </div>

            {editing === ex.exchange && (
              <div style={{ marginTop: 10, paddingLeft: 12, borderLeft: '2px solid var(--border-strong)' }}>
                <div className="field">
                  <label>API key</label>
                  <input type="text" value={apiKey} onChange={(e) => setApiKey(e.target.value)} autoComplete="off" />
                </div>
                <div className="field">
                  <label>API secret</label>
                  <input
                    type="password"
                    value={secret}
                    onChange={(e) => setSecret(e.target.value)}
                    autoComplete="new-password"
                  />
                </div>
                {ex.requiresPassword && (
                  <div className="field">
                    <label>API passphrase</label>
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      autoComplete="new-password"
                    />
                    <div className="help">{ex.label} requires a passphrase in addition to key and secret.</div>
                  </div>
                )}
                {error && <div className="notice bad">{error}</div>}
                <button
                  className="btn primary small"
                  onClick={() => void submit(ex.exchange)}
                  disabled={saving || !apiKey || !secret}
                >
                  {saving ? 'Saving…' : 'Save keys'}
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------------- 2FA

function TwoFactor() {
  const [enabled, setEnabled] = useState(false);
  const [provisioning, setProvisioning] = useState<{ secret: string; provisioningUri: string } | null>(null);
  const [code, setCode] = useState('');
  const [message, setMessage] = useState<{ kind: 'ok' | 'bad'; text: string } | null>(null);

  useEffect(() => {
    void api.me().then((me) => setEnabled(me.totpEnabled));
  }, []);

  const begin = async () => {
    setMessage(null);
    setProvisioning(await api.beginTotp());
  };

  const enable = async () => {
    try {
      await api.enableTotp(code);
      setEnabled(true);
      setProvisioning(null);
      setCode('');
      setMessage({ kind: 'ok', text: 'Two-factor authentication is on.' });
    } catch (err) {
      setMessage({ kind: 'bad', text: err instanceof Error ? err.message : 'could not enable' });
    }
  };

  const disable = async () => {
    await api.disableTotp();
    setEnabled(false);
    setMessage({ kind: 'ok', text: 'Two-factor authentication is off.' });
  };

  return (
    <div className="card">
      <div className="card-head">
        <h2>Two-factor authentication</h2>
        <span className={enabled ? 'pill ok' : 'pill'}>{enabled ? 'enabled' : 'disabled'}</span>
      </div>
      <div className="card-body">
        {message && <div className={`notice ${message.kind}`}>{message.text}</div>}

        {enabled ? (
          <>
            <p className="dim" style={{ marginTop: 0 }}>
              A code from your authenticator app is required at every login.
            </p>
            <button className="btn small" onClick={() => void disable()}>
              Disable
            </button>
          </>
        ) : provisioning ? (
          <>
            <p className="dim" style={{ marginTop: 0 }}>
              Add this secret to an authenticator app, then enter the current code to confirm it
              works. Two-factor is not switched on until a code verifies.
            </p>
            <div className="field">
              <label>Secret</label>
              <input type="text" readOnly value={provisioning.secret} onFocus={(e) => e.target.select()} />
            </div>
            <div className="field">
              <label>Setup URI</label>
              <input
                type="text"
                readOnly
                value={provisioning.provisioningUri}
                onFocus={(e) => e.target.select()}
              />
            </div>
            <div className="row">
              <div style={{ width: 140 }}>
                <label>Current code</label>
                <input type="text" inputMode="numeric" value={code} onChange={(e) => setCode(e.target.value)} />
              </div>
              <button className="btn primary" onClick={() => void enable()} disabled={code.length < 6}>
                Verify and enable
              </button>
            </div>
          </>
        ) : (
          <>
            <p className="dim" style={{ marginTop: 0 }}>
              Optional, and worth it: this login is the only thing between the internet and an
              interface that places live orders.
            </p>
            <button className="btn small" onClick={() => void begin()}>
              Set up
            </button>
          </>
        )}
      </div>
    </div>
  );
}
